/**
 * Exam countdown maths for the sidebar card.
 *
 * The progress bar represents *time elapsed* toward the exam over a fixed
 * 90-day prep window (product-approved): 90+ days out ≈ 0 %, exam day = 100 %.
 *
 * `now` is injected (never read from `Date.now()` internally) so the helper is
 * pure and unit-testable — the test env freezes/blocks the ambient clock.
 */

/** Fixed preparation window the progress bar is measured against. */
export const PREP_WINDOW_DAYS = 90;

const MS_PER_DAY = 86_400_000;

export interface ExamCountdown {
  /** Whole days until the exam, clamped at 0 (never negative for past dates). */
  daysLeft: number;
  /** Percentage of the 90-day prep window elapsed, clamped to [0, 100]. */
  pct: number;
}

/**
 * Compute the day count + progress percentage from a `YYYY-MM-DD` exam date.
 *
 * Both dates are collapsed to midnight via `Date.UTC` (using the *local*
 * calendar components of `now`), so the difference is always an exact multiple
 * of a day — DST-safe and independent of the runner's timezone.
 */
export function computeExamCountdown(examDate: string, now: Date): ExamCountdown {
  const [year, month, day] = examDate.split('-').map(Number);
  const examMidnight = Date.UTC(year, month - 1, day);
  const todayMidnight = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

  const daysLeft = Math.max(0, Math.ceil((examMidnight - todayMidnight) / MS_PER_DAY));
  const pct = Math.max(0, Math.min(100, (1 - daysLeft / PREP_WINDOW_DAYS) * 100));

  return { daysLeft, pct };
}

/**
 * Whether the exam is today or in the future (relative to `now`'s local day).
 * A strictly past date returns `false` — the sidebar then shows the CTA rather
 * than a stuck "0 days" card, and the profile form re-seeds empty.
 * Comparison is done at UTC midnight (see `computeExamCountdown`) for
 * DST-/timezone-safe whole-day semantics.
 */
export function hasUpcomingExam(examDate: string, now: Date): boolean {
  const [year, month, day] = examDate.split('-').map(Number);
  const examMidnight = Date.UTC(year, month - 1, day);
  const todayMidnight = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return examMidnight >= todayMidnight;
}

/** Which of the three sidebar exam-card states applies to a stored date. */
export type ExamCardState =
  /** No date scheduled yet → generic "plan your exam" CTA. */
  | 'none'
  /** A date exists but is already in the past → "lapsed" / reschedule state. */
  | 'lapsed'
  /** Today or in the future → populated countdown card. */
  | 'upcoming';

/**
 * Classify a (possibly absent) stored exam date into its card state. Kept pure
 * and separate from rendering so the three-way branch is unit-testable.
 */
export function examCardState(examDate: string | null | undefined, now: Date): ExamCardState {
  if (!examDate) return 'none';
  return hasUpcomingExam(examDate, now) ? 'upcoming' : 'lapsed';
}

/**
 * Format a `YYYY-MM-DD` exam date for display, building the Date from its
 * numeric parts (local midnight) rather than parsing the string — parsing
 * `new Date("YYYY-MM-DD")` yields UTC midnight and shifts a day earlier for
 * viewers at negative UTC offsets. Component-built dates match the stored
 * calendar date in every timezone.
 */
export function formatExamDate(examDate: string, locale: string): string {
  const [year, month, day] = examDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale);
}
